import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import { 
  UserPlus, 
  Search, 
  Filter, 
  Eye,
  Edit,
  Trash2,
  Plus,
  X,
  FileText,
  Calendar,
  Mail,
  Phone,
  Briefcase,
  CheckCircle,
  Clock,
  XCircle,
  Users,
  Target,
  TrendingUp,
  LayoutGrid,
  Shield,
  Download,
  Upload,
  FileSpreadsheet,
  RefreshCcw,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  FileOutput,
  User,
  GraduationCap,
  LayoutList,
  List,
  Check
} from 'lucide-react';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import FacturationListPageLayout, {
  facturationTableScrollClass,
} from '../facturation/components/FacturationListPageLayout';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import {
  buildHrColumnVisibilityState,
  buildHrQawaemColumnDefs,
  buildHrImportExportProps,
  buildHrControlsStrip,
  isHrColumnVisible,
  createHrHandleToggleColumn,
  hrTableColSpan,
  useHrModalBodyLock,
  HrFormModalTabBar,
  useHrTableRowSelection,
  HrBulkDeleteFilterAction,
  sliceHrListItems,
  useHrDepartments,
} from './hrListPageUtils';
import { useHrConfig } from './useHrConfig';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import localApiAdapter from '../../core/services/localApiAdapter';
import { useHR } from '../../core/contexts/HRContext';
import { formatBrandMoney, getBrandLocale, getBrandDatePickerLocale } from '../../shared/utils/brandCurrency';
import {
  RECRUITMENT_CHANNELS,
  NITAQAT_LEVELS,
  getDefaultSaudiRecruitmentFields,
} from './utils/hrSaudiFormat';
import DatePicker from 'react-datepicker';
import { registerLocale } from 'react-datepicker';
import ar from 'date-fns/locale/ar-SA';
import 'react-datepicker/dist/react-datepicker.css';
import { exportToExcel, triggerFileInput, importFromFile } from '../../shared/utils/importExportUtils';
import { generateReportPDF } from '../../shared/utils/reportPdfGenerator';
import { generateReportExcel } from '../../shared/utils/reportExcelGeneratorAdvanced';
import { CustomSelect, CustomDatePicker, CustomNumberInput } from '../../shared/components/custom';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';
import { mergeStickySelectClass } from '../../products/saudi/components/QawaemTableStickyActions';

registerLocale('ar', ar);

const HR_TABLE_COLUMNS = [
  { key: 'candidate', labelKey: 'col_candidate' },
  { key: 'position', labelKey: 'col_position' },
  { key: 'contact', labelKey: 'card_contact' },
  { key: 'experience', labelKey: 'col_experience' },
  { key: 'appliedDate', labelKey: 'col_applied_date' },
  { key: 'status', labelKey: 'col_status' },
  { key: 'actions', labelKey: 'col_actions', required: true },
];

const RecruitmentPage = () => {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`modules.ressources_humaines.pages.recruitmentPage.${key}`, opts);
  const qawaem = isSaudiBrand();
  const { isDashboardLightMode } = useGlobalTheme();
  const dateLocale = getBrandLocale(i18n.language);
  const datePickerLocale = getBrandDatePickerLocale();
  const saudiRecruitmentDefaults = getDefaultSaudiRecruitmentFields();
  const hrConfig = useHrConfig();
  // Use HR Context for real data
  const { 
    candidates, 
    addCandidate, 
    updateCandidate, 
    removeCandidate, 
    refreshData,
    loading 
  } = useHR();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingCandidate, setEditingCandidate] = useState(null);
  const [viewingCandidate, setViewingCandidate] = useState(null);
  useHrModalBodyLock(showModal, showViewModal);
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [viewMode, setViewMode] = useState('table');
  
  // Main view mode state
  const [mainViewMode, setMainViewMode] = useState('all_candidates');
  const [quickFilter, setQuickFilter] = useState('all');
  
  // Advanced options toggle
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  
  // Export states
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isGeneratingExcel, setIsGeneratingExcel] = useState(false);
  
  // Date range filter
  // Dropdown states and refs
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const [formTab, setFormTab] = useState('candidate');
  

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    position: '',
    department: '',
    departmentId: '',
    experience: '',
    education: '',
    status: 'nouveau',
    appliedDate: new Date().toISOString().split('T')[0],
    cvUrl: '',
    notes: '',
    ...saudiRecruitmentDefaults,
  });

  const triggerRefresh = () => refreshData();

  const { departments } = useHrDepartments();

  const departmentSelectOptions = useMemo(() => [
    { value: '', label: p('select_department', { defaultValue: 'اختر القسم' }) },
    ...departments.map((d) => ({ value: d.id, label: d.name })),
  ], [departments, p]);

  const positionSelectOptions = useMemo(() => {
    const fromCandidates = candidates.map((c) => c.position).filter(Boolean);
    const fromDepts = departments.map((d) => d.name).filter(Boolean);
    return [...new Set([...fromDepts, ...fromCandidates, ...(formData.position ? [formData.position] : [])])]
      .map((pos) => ({ value: pos, label: pos }));
  }, [candidates, departments, formData.position]);

  // Calculate stats
  const stats = {
    total: candidates.length,
    nouveaux: candidates.filter(c => c.status === 'nouveau').length,
    entretien: candidates.filter(c => c.status === 'entretien').length,
    acceptes: candidates.filter(c => c.status === 'accepte').length,
    refuses: candidates.filter(c => c.status === 'refuse').length
  };

  // NEW: Calculate candidate distribution
  const candidateDistribution = {
    all_candidates: {
      title: p('dist_overview'),
      data: [
        { filterKey: 'nouveau', name: p('dist_nouveaux'), count: stats.nouveaux, color: "bg-gradient-to-r from-orange-500 to-orange-600" },
        { filterKey: 'entretien', name: p('dist_entretien'), count: stats.entretien, color: "bg-gradient-to-r from-purple-500 to-purple-600" },
        { filterKey: 'accepte', name: p('dist_acceptes'), count: stats.acceptes, color: "bg-gradient-to-r from-green-500 to-green-600" },
        { filterKey: 'refuse', name: p('dist_refuses'), count: stats.refuses, color: "bg-gradient-to-r from-red-500 to-red-600" }
      ]
    },
    par_statut: {
      title: p('dist_by_status_title'),
      data: [
        { filterKey: 'nouveau', name: p('dist_nouveaux'), count: stats.nouveaux, color: "bg-gradient-to-r from-orange-500 to-orange-600" },
        { filterKey: 'entretien', name: p('dist_entretien'), count: stats.entretien, color: "bg-gradient-to-r from-purple-500 to-purple-600" },
        { filterKey: 'accepte', name: p('dist_acceptes'), count: stats.acceptes, color: "bg-gradient-to-r from-green-500 to-green-600" },
        { filterKey: 'refuse', name: p('dist_refuses'), count: stats.refuses, color: "bg-gradient-to-r from-red-500 to-red-600" }
      ]
    },
    par_poste: {
      title: p('dist_by_position_title'),
      data: [...new Set(candidates.map(c => c.position))].map(position => ({
        filterKey: position,
        name: position,
        count: candidates.filter(c => c.position === position).length,
        color: "bg-gradient-to-r from-blue-500 to-blue-600"
      }))
    },
    par_date: {
      title: p('dist_by_month_title'),
      data: (() => {
        const monthCounts = {};
        candidates.forEach(c => {
          const month = new Date(c.appliedDate).toLocaleDateString(dateLocale, { year: 'numeric', month: 'long' });
          monthCounts[month] = (monthCounts[month] || 0) + 1;
        });
        return Object.entries(monthCounts).map(([month, count]) => ({
          filterKey: month,
          name: month,
          count,
          color: "bg-gradient-to-r from-indigo-500 to-indigo-600"
        }));
      })()
    }
  };

  const distributionInfo = candidateDistribution[mainViewMode] || candidateDistribution.all_candidates;

  // NEW: Main view tabs
  const mainTabs = [
    { id: 'all_candidates', name: p('tab_general'), icon: LayoutGrid },
    { id: 'par_statut', name: p('tab_by_status'), icon: Shield },
    { id: 'par_poste', name: p('tab_by_position'), icon: Briefcase },
    { id: 'par_date', name: p('tab_by_date'), icon: Calendar }
  ];

  const quickFilterButtons = [
    { id: 'all', name: p('filter_all'), icon: Users },
    { id: 'nouveau', name: p('filter_nouveau'), icon: Clock },
    { id: 'entretien', name: p('filter_entretien'), icon: FileText },
    { id: 'accepte', name: p('filter_accepte'), icon: CheckCircle },
    { id: 'refuse', name: p('filter_refuse'), icon: XCircle }
  ];

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) {
        setShowImportDropdown(false);
      }
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setShowExportDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const baseFilteredCandidates = useMemo(() => candidates.filter((candidate) => {
    const matchesSearch = !searchTerm ||
      candidate.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      candidate.position?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      candidate.email?.toLowerCase().includes(searchTerm.toLowerCase());

    if (!matchesSearch) return false;

    if (quickFilter !== 'all' && candidate.status !== quickFilter) return false;
    return true;
  }), [candidates, searchTerm, quickFilter]);

  const columnFilterRules = useMemo(() => [
    textColumnRule('candidate', (r) => r.name || ''),
    textColumnRule('position', (r) => r.position || ''),
    textColumnRule('contact', (r) => `${r.email || ''} ${r.phone || ''}`.trim()),
    textColumnRule('experience', (r) => r.experience || ''),
    textColumnRule('appliedDate', (r) => (r.appliedDate ? new Date(r.appliedDate).toLocaleDateString(dateLocale) : '')),
    exactColumnRule('status', (r) => r.status || ''),
  ], [dateLocale]);

  const filteredCandidates = useMemo(
    () => sliceHrListItems(
      applyColumnFilters(baseFilteredCandidates, colFilters, columnFilterRules),
      hrConfig.itemsPerPage,
    ),
    [baseFilteredCandidates, colFilters, columnFilterRules, hrConfig.itemsPerPage],
  );

  const { allRowsSelected, someRowsSelected, handleSelectAll, toggleRecord: handleSelectRecord } =
    useHrTableRowSelection(filteredCandidates, selectedCandidates, setSelectedCandidates);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const deptNote = formData.department ? `${p('field_department', { defaultValue: 'القسم' })}: ${formData.department}` : '';
    const notes = [deptNote, formData.notes].filter(Boolean).join('\n');
    const payload = { ...formData, notes };
    try {
      if (editingCandidate) {
        await updateCandidate(editingCandidate.id, payload);
        notifyUpdated(showNotify, p('entity_label', { defaultValue: 'المرشح' }));
      } else {
        await addCandidate(payload);
        notifyCreated(showNotify, p('entity_label', { defaultValue: 'المرشح' }));
      }
      resetForm();
    } catch (error) {
      console.error('Error saving candidate:', error);
      notifyError(showNotify, p('save_error_candidate'));
    }
  };

  const handleEdit = (candidate) => {
    setEditingCandidate(candidate);
    setFormData(candidate);
    setFormTab('candidate');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('delete_confirm'))) {
      try {
        await removeCandidate(id);
        notifyDeleted(showNotify, p('entity_label', { defaultValue: 'المرشح' }));
      } catch (error) {
        console.error('Error deleting candidate:', error);
        notifyError(showNotify, p('alert_delete_error'));
      }
    }
  };

  const handleView = (candidate) => {
    setViewingCandidate(candidate);
    setShowViewModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      position: '',
      department: '',
      departmentId: '',
      experience: '',
      education: '',
      status: 'nouveau',
      appliedDate: new Date().toISOString().split('T')[0],
      cvUrl: '',
      notes: '',
      ...getDefaultSaudiRecruitmentFields(),
    });
    setEditingCandidate(null);
    setFormTab('candidate');
    setShowModal(false);
  };

  const openNewCandidateModal = () => {
    setEditingCandidate(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      position: '',
      department: '',
      departmentId: '',
      experience: '',
      education: '',
      status: 'nouveau',
      appliedDate: new Date().toISOString().split('T')[0],
      cvUrl: '',
      notes: '',
      ...getDefaultSaudiRecruitmentFields(),
    });
    setFormTab('candidate');
    setShowModal(true);
  };

  const handleBulkDelete = async () => {
    if (window.confirm(p('bulk_delete_confirm', { count: selectedCandidates.length }))) {
      try {
        const deleteCount = selectedCandidates.length;
        await Promise.all(selectedCandidates.map(id => removeCandidate(id)));
        notifyBulkDeleted(showNotify, deleteCount, p('entity_label_plural', { defaultValue: 'مرشح' }));
        setSelectedCandidates([]);
      } catch (error) {
        console.error('Error bulk deleting:', error);
        notifyError(showNotify, p('alert_delete_error'));
      }
    }
  };

  const handleExport = () => {
    const headers = [
      { key: 'name', label: p('export_col_name') },
      { key: 'position', label: p('col_position') },
      { key: 'email', label: p('label_email') },
      { key: 'phone', label: p('export_col_phone') },
      { key: 'experience', label: p('col_experience') },
      { key: 'education', label: p('col_education') },
      { key: 'status', label: p('col_status') },
      { key: 'appliedDate', label: p('col_applied_date') }
    ];
    const dataToExport = filteredCandidates;
    const fileName = `candidats_export_${new Date().toISOString().split('T')[0]}`;
    try {
      exportToExcel(dataToExport, headers, `${fileName}.xlsx`);
    } catch (error) {
      console.error('Export error:', error);
      alert(p('alert_export_error'));
    }
  };

  const handleImport = () => {
    const headers = [
      { key: 'name', label: p('export_col_name') },
      { key: 'email', label: p('label_email') },
      { key: 'phone', label: p('export_col_phone') },
      { key: 'position', label: p('col_position') },
      { key: 'experience', label: p('col_experience') },
      { key: 'education', label: p('col_education') },
      { key: 'status', label: p('col_status') },
      { key: 'appliedDate', label: p('col_applied_date') },
      { key: 'cvUrl', label: p('export_col_cv_url') },
      { key: 'notes', label: p('label_notes') }
    ];
    const schema = {
      requiredFields: ['name'],
      validators: {
        status: (value) => !value || ['nouveau', 'entretien', 'accepte', 'refuse'].includes(String(value).toLowerCase())
      }
    };
    triggerFileInput(async (file) => {
      try {
        const result = await importFromFile(file, headers, schema);
        if (result.success) {
          let successCount = 0;
          for (const record of result.data) {
            try {
              const row = {
                name: record.name || '',
                email: record.email || '',
                phone: record.phone || '',
                position: record.position || '',
                experience: record.experience || '',
                education: record.education || '',
                status: record.status && ['nouveau', 'entretien', 'accepte', 'refuse'].includes(String(record.status).toLowerCase())
                  ? String(record.status).toLowerCase()
                  : 'nouveau',
                appliedDate: record.appliedDate || new Date().toISOString().split('T')[0],
                cvUrl: record.cvUrl || '',
                notes: record.notes || ''
              };
              await addCandidate(row);
              successCount++;
            } catch (err) {
              console.error('Import candidate row error:', err);
            }
          }
          await refreshData();
          const message = p('import_success_msg', {
            total: result.totalCount,
            imported: successCount,
            errors: result.invalidCount > 0 ? `\n${p('alert_import_error')}: ${result.invalidCount}` : '',
          });
          alert(message);
        }
      } catch (error) {
        console.error('Import error:', error);
        alert(p('alert_import_error'));
      }
    }, '.csv,.xlsx,.xls');
  };

  const handleExportPDF = async () => {
    try {
      setIsGeneratingPDF(true);
      const reportData = filteredCandidates.map(c => ({
        [p('export_col_name')]: c.name,
        [p('col_position')]: c.position,
        [p('label_email')]: c.email,
        [p('export_col_phone')]: c.phone,
        [p('col_experience')]: c.experience,
        [p('col_education')]: c.education,
        [p('col_status')]: getStatusLabel(c.status),
        [p('col_applied_date')]: new Date(c.appliedDate).toLocaleDateString(dateLocale),
        [p('label_recruitment_cost')]: formatBrandMoney(c.recruitmentCost || 0),
      }));

      await generateReportPDF(p('report_title'), reportData, { orientation: 'landscape' });
    } catch (error) {
      console.error('PDF export error:', error);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      setIsGeneratingExcel(true);
      const reportData = filteredCandidates.map(c => ({
        [p('export_col_name')]: c.name,
        [p('col_position')]: c.position,
        [p('label_email')]: c.email,
        [p('export_col_phone')]: c.phone,
        [p('col_experience')]: c.experience,
        [p('col_education')]: c.education,
        [p('col_status')]: getStatusLabel(c.status),
        [p('col_applied_date')]: new Date(c.appliedDate).toLocaleDateString(dateLocale),
        [p('label_recruitment_cost')]: formatBrandMoney(c.recruitmentCost || 0),
      }));

      await generateReportExcel(p('report_title'), reportData);
    } catch (error) {
      console.error('Excel export error:', error);
    } finally {
      setIsGeneratingExcel(false);
    }
  };

  const statusOptions = [
    { value: 'nouveau', label: p('status_nouveau'), icon: Clock, style: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
    { value: 'entretien', label: p('status_entretien'), icon: FileText, style: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
    { value: 'accepte', label: p('status_accepte'), icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'refuse', label: p('status_refuse'), icon: XCircle, style: 'bg-red-500/20 text-red-400 border-red-500/30' }
  ];

  const qawaemStatusOptions = useMemo(
    () => [
      { value: 'all', label: p('filter_all') },
      ...statusOptions.map((s) => ({ value: s.value, label: s.label })),
    ],
    [p],
  );

  const listStats = useMemo(
    () => [
      { key: 'total', label: p('stat_total'), value: stats.total, icon: Users, variant: 'copper' },
      { key: 'nouveaux', label: p('stat_nouveaux'), value: stats.nouveaux, icon: Clock, variant: 'forest' },
      { key: 'entretien', label: p('stat_entretien'), value: stats.entretien, icon: FileText, variant: 'copper' },
      { key: 'acceptes', label: p('stat_acceptes'), value: stats.acceptes, icon: CheckCircle, variant: 'forest' },
    ],
    [stats, p],
  );

  const [columnVisibility, setColumnVisibility] = useState(() =>
    buildHrColumnVisibilityState(HR_TABLE_COLUMNS),
  );
  const qawaemColumns = useMemo(
    () => buildHrQawaemColumnDefs(HR_TABLE_COLUMNS, p, columnVisibility),
    [p, columnVisibility],
  );
  const handleToggleColumn = createHrHandleToggleColumn(setColumnVisibility, HR_TABLE_COLUMNS);
  const qawaemVisibleColumnCount = qawaemColumns.filter((c) => c.checked !== false).length;
  const isColVisible = (key) => isHrColumnVisible(columnVisibility, key);
  const tableColSpan = hrTableColSpan(columnVisibility);

  const importExportProps = useMemo(
    () => buildHrImportExportProps({
      showImportDropdown,
      setShowImportDropdown,
      showExportDropdown,
      setShowExportDropdown,
      importDropdownRef,
      exportDropdownRef,
      p,
      t,
      isGeneratingPDF,
      handlers: {
        onImport: handleImport,
        onExportCSV: handleExport,
        onExportExcel: handleExportExcel,
        onExportPDF: handleExportPDF,
      },
    }),
    [showImportDropdown, showExportDropdown, isGeneratingPDF, p, t],
  );

  const candidateStatusFilterOptions = useMemo(
    () => statusOptions.map((s) => ({ value: s.value, label: s.label })),
    [statusOptions],
  );

  const recruitmentFormTabs = useMemo(() => ([
    { id: 'candidate', label: p('section_personal'), icon: User, iconClassName: 'text-blue-400' },
    { id: 'process', label: p('section_status'), icon: CheckCircle, iconClassName: 'text-purple-400' },
  ]), [p]);

  const advancedOptionsContent = (
    <div className="space-y-6 pt-6">
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20 shadow-xl shadow-black/10">
        <div className="flex space-x-1 overflow-x-auto table-scroll">
          {mainTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setMainViewMode(tab.id);
                setQuickFilter('all');
              }}
              className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                mainViewMode === tab.id
                  ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg'
                  : 'text-white/80 hover:bg-white/10 hover:text-white'
              }`}
            >
              <tab.icon className="h-5 w-5" />
              <span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
            </button>
          ))}
        </div>
      </div>
      {mainViewMode === 'all_candidates' && (
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20 shadow-xl shadow-black/10">
          <div className="flex space-x-1 overflow-x-auto table-scroll">
            {quickFilterButtons.map((filter) => (
              <button
                key={filter.id}
                onClick={() => {
                  setQuickFilter(filter.id);
                }}
                className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                  quickFilter === filter.id
                    ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg'
                    : 'text-white/80 hover:bg-white/10 hover:text-white'
                }`}
              >
                <filter.icon className="h-5 w-5" />
                <span className="hidden sm:inline whitespace-nowrap">{filter.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 shadow-xl shadow-black/10"
      >
        <h3 className="text-xl font-semibold text-white mb-4">{distributionInfo.title}</h3>
        <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '3')}>{distributionInfo.data.map((item) => (
          <div key={item.name} className="bg-white/5 rounded-xl p-4 border border-white/10">
            <div className="flex justify-between items-center mb-2">
              <h4 className="font-medium text-white">{item.name}</h4>
              <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white`}>{item.count}</div>
            </div>
            <div className="w-full bg-white/10 h-3 rounded-full">
              <div
                className={`h-3 rounded-full ${item.color}`}
                style={{
                  width: `${(() => {
                    const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                    return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                  })()}%`,
                }}
              />
            </div>
            <p className="mt-2 text-sm text-white/60">
              {p('percent_of_items', { percent: (() => {
                const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
              })() })}
            </p>
          </div>
        ))}
        </div>
      </motion.div>
    </div>
  );

  const getStatusLabel = (status) => {
    const opt = statusOptions.find(s => s.value === status);
    return opt ? opt.label : p('na');
  };

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      await localApiAdapter.put(`/hr/recruitment/${recordId}`, { status: newStatus });      await refreshData();
      if (viewingCandidate && viewingCandidate.id === recordId) {
        setViewingCandidate((prev) => (prev ? { ...prev, status: newStatus } : null));
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const getStatusBadge = (status) => {
    const config = statusOptions.find(s => s.value === status) || statusOptions[0];
    const { label, icon: Icon, style } = config;
    return <span className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border ${style}`}><Icon size={14} />{label}</span>;
  };


  return (
    <>
      <FacturationListPageLayout
        title={p('title')}
        subtitle={p('subtitle')}
        icon={UserPlus}
        stats={listStats}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        filterActions={
          <HrBulkDeleteFilterAction
            count={selectedCandidates.length}
            onDelete={handleBulkDelete}
            label={p('delete_count', { count: selectedCandidates.length })}
          />
        }
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder={p('search_placeholder', { defaultValue: t('common.search', { defaultValue: 'بحث...' }) })}
        columns={qawaemColumns}
        visibleColumnCount={qawaemVisibleColumnCount}
        totalColumnCount={qawaemColumns.length}
        onCreate={openNewCandidateModal}
        createLabel={p('new_item')}
        importExport={importExportProps}
        headerActions={
          <button type="button" onClick={triggerRefresh} className="refresh-button" title={p('refresh')}>
            <RefreshCcw size={20} />
          </button>
        }
        showAdvancedOptions={showAdvancedOptions}
        onToggleAdvancedOptions={() => setShowAdvancedOptions(!showAdvancedOptions)}
        advancedOptionsLabel={p('advanced_options')}
        advancedOptionsContent={advancedOptionsContent}
        controlsStrip={buildHrControlsStrip({
          qawaem,
          viewMode,
          onViewModeChange: setViewMode,
          isDashboardLightMode,
          searchTerm,
          onSearchChange: setSearchTerm,
          searchPlaceholder: p('search_placeholder', { defaultValue: t('common.search', { defaultValue: 'بحث...' }) }),
          cardsTitle: t('common.view_cards', { defaultValue: 'بطاقات' }),
          tableTitle: t('common.view_table', { defaultValue: 'جدول' }),
        })}
      >
        {viewMode === 'table' && (
          <div className={facturationTableScrollClass(qawaem)}>
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <th className={mergeStickySelectClass('px-3 py-3 text-center align-middle')}>
                    <div className="flex justify-center">
                      <QawaemTableCheckbox size="lg" checked={allRowsSelected} indeterminate={someRowsSelected} onChange={handleSelectAll} />
                    </div>
                  </th>
                  {isColVisible('candidate') && <TableColumnHeaderFilter columnKey="candidate" label={p('col_candidate')} value={colFilters.candidate ?? ''} onChange={(v) => setColFilter('candidate', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" />}
                  {isColVisible('position') && <TableColumnHeaderFilter columnKey="position" label={p('col_position')} value={colFilters.position ?? ''} onChange={(v) => setColFilter('position', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" />}
                  {isColVisible('contact') && <TableColumnHeaderFilter columnKey="contact" label={p('card_contact')} value={colFilters.contact ?? ''} onChange={(v) => setColFilter('contact', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" />}
                  {isColVisible('experience') && <TableColumnHeaderFilter columnKey="experience" label={p('col_experience')} value={colFilters.experience ?? ''} onChange={(v) => setColFilter('experience', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" />}
                  {isColVisible('appliedDate') && <TableColumnHeaderFilter columnKey="appliedDate" label={p('col_applied_date')} value={colFilters.appliedDate ?? ''} onChange={(v) => setColFilter('appliedDate', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" />}
                  {isColVisible('status') && <TableColumnHeaderFilter columnKey="status" label={p('col_status')} kind="select" options={candidateStatusFilterOptions} value={colFilters.status ?? ''} onChange={(v) => setColFilter('status', v)} align="center" editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" />}
                  <QawaemTableHeaderActionsCell label={p('col_actions')} columns={qawaemColumns} onToggle={handleToggleColumn} visibleCount={qawaemVisibleColumnCount} totalCount={qawaemColumns.length} thClassName="px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle" />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {filteredCandidates.length > 0 ? filteredCandidates.map((candidate, index) => (
                  <tr key={candidate.id} className={`hover:bg-white/5 transition-colors ${selectedCandidates.includes(candidate.id) ? 'bg-white/10' : ''}`}>
                    <td className={mergeStickySelectClass('px-3 py-4 text-center align-middle')}><div className="flex justify-center"><QawaemTableCheckbox checked={selectedCandidates.includes(candidate.id)} onChange={() => handleSelectRecord(candidate.id)} /></div></td>
                    {isColVisible('candidate') && <td className="px-6 py-4"><p className="text-white font-semibold">{candidate.name}</p><p className="text-white/60 text-sm">{candidate.education}</p></td>}
                    {isColVisible('position') && <td className="px-6 py-4 text-white/80">{candidate.position}</td>}
                    {isColVisible('contact') && <td className="px-6 py-4"><div className="text-white/80 text-sm">{candidate.email}</div><div className="text-white/60 text-sm">{candidate.phone}</div></td>}
                    {isColVisible('experience') && <td className="px-6 py-4 text-white/80">{candidate.experience}</td>}
                    {isColVisible('appliedDate') && <td className="px-6 py-4 text-white/80">{candidate.appliedDate ? new Date(candidate.appliedDate).toLocaleDateString(dateLocale) : '—'}</td>}
                    {isColVisible('status') && <td className="px-6 py-4 text-center align-middle"><div className="flex justify-center"><QawaemClickableStatusBadge status={candidate.status} recordId={candidate.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status')} /></div></td>}
                    <td className="px-6 py-4 text-center align-middle"><QawaemErpRowActions onView={() => handleView(candidate)} onEdit={() => handleEdit(candidate)} onDelete={() => handleDelete(candidate.id)} viewTitle={p('action_view')} editTitle={p('action_edit')} deleteTitle={p('action_delete')} /></td>
                  </tr>
                )) : (
                  <tr><td colSpan={tableColSpan} className="text-center py-12 text-white/60"><UserPlus size={48} className="mx-auto mb-4 text-white/30" /><p className="text-lg">{p('empty_title')}</p></td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {filteredCandidates.length > 0 ? filteredCandidates.map((candidate, index) => (
              <motion.div key={candidate.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 h-full min-w-0">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0"><h3 className="text-xl font-bold text-white">{candidate.name}</h3><p className="text-white/70">{candidate.position}</p></div>
                  <QawaemErpRowActions compact onView={() => handleView(candidate)} onEdit={() => handleEdit(candidate)} onDelete={() => handleDelete(candidate.id)} viewTitle={p('action_view')} editTitle={p('action_edit')} deleteTitle={p('action_delete')} />
                </div>
              </motion.div>
            )) : <div className="text-center py-12 text-white/50"><UserPlus size={48} className="mx-auto mb-4 opacity-50" /><p>{p('empty_title')}</p></div>}
          </div>
        )}
      </FacturationListPageLayout>

      {/* Add/Edit Modal */}
      {showModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" style={{ top: 0, left: 0, right: 0, bottom: 0 }}>
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }} 
            animate={{ opacity: 1, scale: 1 }} 
            className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl max-h-[90vh] flex flex-col overflow-hidden" 
            style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '90%', maxWidth: '1200px' }}
          >
            {/* Fixed Header */}
            <div className={`flex items-center justify-between p-6 border-b ${isDashboardLightMode ? 'border-gray-200' : 'border-white/10'}`}>
              <h2 className={`text-2xl font-bold ${isDashboardLightMode ? 'text-gray-900' : 'text-white'}`}>
                {editingCandidate ? p('modal_edit') : p('modal_new')}
              </h2>
              <div className="flex items-center gap-3">
                <button 
                  type="submit" 
                  form="candidate-form" 
                  className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30"
                >
                  <CheckCircle size={18} />
                  {editingCandidate ? p('update') : p('create')}
                </button>
                <button onClick={resetForm} className={isDashboardLightMode ? 'text-gray-500 hover:text-gray-800 transition-colors' : 'text-white/70 hover:text-white transition-colors'}>
                  <X size={24} />
                </button>
              </div>
            </div>
            
            
            <HrFormModalTabBar
              tabs={recruitmentFormTabs}
              activeTab={formTab}
              onTabChange={setFormTab}
              isLight={isDashboardLightMode}
              ariaLabel={editingCandidate ? p('modal_edit') : p('modal_new')}
            />

            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="candidate-form" onSubmit={handleSubmit} >
                {formTab === 'candidate' && (
                  <>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{<RequiredLabelText text={p('field_full_name')} />}</label>
                            <input
                              type="text"
                              required
                              value={formData.name}
                              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                              placeholder={p('placeholder_full_name')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{<RequiredLabelText text={p('field_email_required')} />}</label>
                            <input
                              type="email"
                              required
                              value={formData.email}
                              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                              placeholder={p('placeholder_email')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_phone')} <span className="qawaem-required-mark">*</span></label>
                            <input
                              type="tel"
                              required
                              value={formData.phone}
                              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                              placeholder={p('placeholder_phone')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('application_date')}</label>
                            <CustomDatePicker
                              value={formData.appliedDate}
                              onChange={(value) => setFormData({ ...formData, appliedDate: value })}
                              placeholder={p('placeholder_date')}
                            />
                          </div>
                    </div>
                  </>
                )}
                {formTab === 'candidate' && (
                  <>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                      <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_department', { defaultValue: 'القسم' })}</label>
                              onChange={(value) => {
                                const dept = departments.find((d) => d.id === value);
                                setFormData({
                                  ...formData,
                                  departmentId: value,
                                  department: dept?.name || '',
                                });
                              }}
                              options={departmentSelectOptions}
                              placeholder={p('select_department', { defaultValue: 'اختر القسم' })}
                              searchable
                              creatable={false}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_position')}</label>
                            <CustomSelect
                              value={formData.position}
                              onChange={(value) => setFormData({ ...formData, position: value })}
                              options={[
                                { value: '', label: p('select_placeholder') },
                                ...positionSelectOptions,
                              ]}
                              placeholder={p('placeholder_position')}
                              searchable={true}
                              creatable={true}
                            />
                            <p className="text-white/50 text-xs mt-1">{p('select_position_hint')}</p>
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_experience')}</label>
                            <input
                              type="text"
                              value={formData.experience}
                              onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                              placeholder={p('placeholder_experience')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_education')}</label>
                            <input
                              type="text"
                              value={formData.education}
                              onChange={(e) => setFormData({ ...formData, education: e.target.value })}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                              placeholder={p('placeholder_education')}
                            />
                          </div>
                    </div>
                  </>
                )}
                {formTab === 'process' && (
                  <>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_nationality')}</label>
                            <input
                              type="text"
                              value={formData.nationality || ''}
                              onChange={(e) => setFormData({ ...formData, nationality: e.target.value, isSaudi: e.target.value === 'SA' })}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white"
                              placeholder="SA"
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_nitaqat')}</label>
                            <CustomSelect
                              value={formData.nitaqatLevel || ''}
                              onChange={(value) => setFormData({ ...formData, nitaqatLevel: value })}
                              options={NITAQAT_LEVELS.map((n) => ({ value: n.value, label: p(n.labelKey) }))}
                              placeholder={p('label_saudization')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_recruitment_channel')}</label>
                            <CustomSelect
                              value={formData.recruitmentChannel || ''}
                              onChange={(value) => setFormData({ ...formData, recruitmentChannel: value })}
                              options={RECRUITMENT_CHANNELS.map((c) => ({ value: c.value, label: p(c.labelKey) }))}
                              placeholder={p('label_recruitment_channel')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_recruitment_cost')}</label>
                            <CustomNumberInput
                              value={formData.recruitmentCost}
                              onChange={(value) => setFormData({ ...formData, recruitmentCost: value })}
                              placeholder="0"
                              min={0}
                            />
                          </div>
                          <div className="flex flex-col gap-2">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input type="checkbox" checked={!!formData.workPermitRequired} onChange={(e) => setFormData({ ...formData, workPermitRequired: e.target.checked })} className="w-5 h-5 rounded" />
                              <span className="text-white text-sm">{p('label_work_permit')}</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input type="checkbox" checked={!!formData.iqamaRequired} onChange={(e) => setFormData({ ...formData, iqamaRequired: e.target.checked })} className="w-5 h-5 rounded" />
                              <span className="text-white text-sm">{p('label_iqama')}</span>
                            </label>
                          </div>
                          <div className="flex flex-col gap-2">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input type="checkbox" checked={!!formData.medicalFitness} onChange={(e) => setFormData({ ...formData, medicalFitness: e.target.checked })} className="w-5 h-5 rounded" />
                              <span className="text-white text-sm">{p('label_medical_fitness')}</span>
                            </label>
                          </div>
                          <div className="flex flex-col gap-2">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input type="checkbox" checked={!!formData.backgroundCheck} onChange={(e) => setFormData({ ...formData, backgroundCheck: e.target.checked })} className="w-5 h-5 rounded" />
                              <span className="text-white text-sm">{p('label_background_check')}</span>
                            </label>
                          </div>
                    </div>
                  </>
                )}
                {formTab === 'process' && (
                  <>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                      <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('col_status')} <span className="qawaem-required-mark">*</span></label>
                            <CustomSelect
                              value={formData.status}
                              onChange={(value) => setFormData({ ...formData, status: value })}
                              options={[
                                { value: 'nouveau', label: p('status_nouveau_label') },
                                { value: 'entretien', label: p('status_entretien_label') },
                                { value: 'accepte', label: p('status_accepte') },
                                { value: 'refuse', label: p('status_refuse') }
                              ]}
                              placeholder={p('placeholder_status')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('col_applied_date')}</label>
                            <CustomDatePicker
                              value={formData.appliedDate}
                              onChange={(value) => setFormData({ ...formData, appliedDate: value })}
                              placeholder={p('placeholder_date')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_notes')}</label>
                            <textarea
                              value={formData.notes}
                              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                              rows={3}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all resize-none"
                              placeholder={p('placeholder_candidate_notes')}
                            />
                          </div>
                    </div>
                  </>
                )}
              </form>
            </div>
          </motion.div>
        </div>,
        document.body
      )}

      {/* View Details Modal — aligned with EmployeesPage view modal styling */}
      {showViewModal && viewingCandidate && ReactDOM.createPortal(
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4"
            onClick={() => {
              setShowViewModal(false);
              setViewingCandidate(null);
            }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
              style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '95%', maxWidth: '1100px' }}
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">{p('view_details')}</h2>
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    setViewingCandidate(null);
                  }}
                  className="text-white/70 hover:text-white transition-colors"
                >
                  <X size={24} />
                </button>
              </div>

              <div >
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <Users size={18} className="text-blue-400" />
                    {p('section_personal')}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="md:col-span-2">
                      <p className="text-white/60 text-sm mb-1">{p('full_name')}</p>
                      <p className="text-white font-medium text-lg">{viewingCandidate.name || p('na')}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('col_status')}</p>
                      <div className="flex" onClick={(e) => e.stopPropagation()}>
                        <QawaemClickableStatusBadge status={viewingCandidate.status} recordId={viewingCandidate.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status')} />
                      </div>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('application_date')}</p>
                      <p className="text-white font-medium">
                        {viewingCandidate.appliedDate
                          ? new Date(viewingCandidate.appliedDate).toLocaleDateString(dateLocale)
                          : p('na')}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <Briefcase size={18} className="text-green-400" />
                    {p('career_section')}
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('col_position')}</p>
                      <p className="text-white font-medium">{viewingCandidate.position || p('na')}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('col_experience')}</p>
                      <p className="text-white font-medium">{viewingCandidate.experience || p('na')}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('col_education')}</p>
                      <p className="text-white font-medium">{viewingCandidate.education || p('na')}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">CV</p>
                      <p className="text-white font-medium">
                        {viewingCandidate.cvUrl ? (
                          <a
                            href={viewingCandidate.cvUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-300 hover:underline"
                          >
                            {p('view_cv')}
                          </a>
                        ) : (
                          p('na')
                        )}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <Mail size={18} className="text-sky-400" />
                      {p('card_contact')}
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_email')}</p>
                        <p className="text-white font-medium">{viewingCandidate.email || p('na')}</p>
                      </div>
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('export_col_phone')}</p>
                        <p className="text-white font-medium">{viewingCandidate.phone || p('na')}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <FileText size={18} className="text-amber-400" />
                      {p('label_notes')}
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_comments')}</p>
                        <p className="text-white font-medium whitespace-pre-wrap">
                          {viewingCandidate.notes?.trim() ? viewingCandidate.notes : p('no_notes')}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-6">
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    setViewingCandidate(null);
                    handleEdit(viewingCandidate);
                  }}
                  className="px-6 py-2 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-colors"
                >
                  {p('edit')}
                </button>
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    setViewingCandidate(null);
                  }}
                  className="px-6 py-2 rounded-xl bg-white/10 text-white hover:bg-white/20 transition-colors"
                >
                  {p('close')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </>
  );
};

export default RecruitmentPage;


